import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT } from "@/constants/testIds";
import { X, Loader2, Check, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactDialog = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && onClose?.();
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [isOpen, onClose]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus("success");
      setMessage(res.data?.message || "Thanks! Your message has been sent.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      setMessage(msg || "Something went wrong. Please try again.");
    }
  };

  const handleClose = () => {
    onClose?.();
    // reset after close animation
    setTimeout(() => {
      setStatus("idle");
      setMessage("");
    }, 250);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden
          />
          <motion.div
            data-testid={CONTACT.dialog}
            role="dialog"
            aria-modal="true"
            aria-label="Contact Printopia"
            className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-border/70 bg-card shadow-[var(--shadow-elev-2)]"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(600px circle at 15% 0%, rgba(0,229,255,0.1), transparent 55%)" }}
            />
            <div className="relative p-6 sm:p-8">
              <button
                data-testid={CONTACT.close}
                onClick={handleClose}
                aria-label="Close contact form"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.1)]">
                  <Mail className="h-4 w-4 text-neo-cyan" />
                </span>
                <h2 className="font-display text-2xl font-semibold tracking-tight">
                  Get in touch
                </h2>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Questions, partnerships, or press? Send us a message and we'll
                reply to your inbox.
              </p>

              {status === "success" ? (
                <div
                  data-testid={CONTACT.success}
                  className="mt-6 flex items-start gap-3 rounded-2xl border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] p-4"
                >
                  <Check className="mt-0.5 h-5 w-5 text-neo-cyan" />
                  <p className="text-sm text-foreground">{message}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="c-name" className="mb-1.5 block text-sm font-medium text-foreground">Name</label>
                    <input
                      id="c-name"
                      data-testid={CONTACT.name}
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="h-11 w-full rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="c-email" className="mb-1.5 block text-sm font-medium text-foreground">Email</label>
                    <input
                      id="c-email"
                      data-testid={CONTACT.email}
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className="h-11 w-full rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="c-message" className="mb-1.5 block text-sm font-medium text-foreground">Message</label>
                    <textarea
                      id="c-message"
                      data-testid={CONTACT.message}
                      required
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="How can we help?"
                      className="w-full resize-none rounded-[var(--radius)] border border-border/70 bg-background/50 px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-[rgba(0,229,255,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(0,229,255,0.35)]"
                    />
                  </div>

                  {status === "error" && (
                    <p data-testid={CONTACT.error} className="text-xs text-red-300">{message}</p>
                  )}

                  <button
                    data-testid={CONTACT.submit}
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius)] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.16)] text-sm font-medium text-foreground shadow-[var(--glow-cyan)] transition-[background-color,transform] duration-200 hover:bg-[rgba(0,229,255,0.24)] active:scale-[0.99] disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                    ) : (
                      <><Mail className="h-4 w-4" /> Send message</>
                    )}
                  </button>
                  <p className="text-center text-xs text-muted-foreground">
                    Goes straight to support@printopiamarketplace.com
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
